import React, { useState } from 'react';
import { ChevronDown, Sparkles, Check, Loader2 } from 'lucide-react';

/**
 * Splits raw content into thinking process and the final answer.
 * Handles:
 * 1. Partial/incomplete <think> tags streaming token-by-token (e.g. '<', '<th', '<think')
 * 2. Closed <think>...</think>
 * 3. Open <think>... still streaming
 * 4. Case-insensitive tags and partial </think> closing tags
 */
export function extractThinkingContent(raw = '') {
    if (!raw || typeof raw !== 'string') {
        return { thinking: '', answer: '' };
    }

    const trimmed = raw.trimStart();

    // If raw starts with a partial opening tag during streaming, e.g. "<", "<th", "<think"
    const openTag = '<think>';
    if (trimmed.length < openTag.length && openTag.startsWith(trimmed.toLowerCase())) {
        return { thinking: '', answer: '' };
    }

    const thinkStartRegex = /<\s*think\s*>/i;
    const thinkEndRegex = /<\/\s*think\s*>/i;

    let remaining = raw;
    const thinkingBlocks = [];
    const answerBlocks = [];

    while (remaining.length > 0) {
        const startMatch = thinkStartRegex.exec(remaining);
        if (!startMatch) {
            // No more <think> tags. Check if remaining starts with a partial unclosed '<think'
            const remTrimmed = remaining.trimStart();
            if (remTrimmed.length < openTag.length && openTag.startsWith(remTrimmed.toLowerCase())) {
                const preTag = remaining.slice(0, remaining.length - remTrimmed.length);
                if (preTag) answerBlocks.push(preTag);
            } else {
                answerBlocks.push(remaining);
            }
            break;
        }

        // Add any text before <think> to answer
        const beforeThink = remaining.slice(0, startMatch.index);
        if (beforeThink) {
            answerBlocks.push(beforeThink);
        }

        const afterStart = remaining.slice(startMatch.index + startMatch[0].length);
        const endMatch = thinkEndRegex.exec(afterStart);

        if (!endMatch) {
            // Still actively streaming inside an unclosed <think> tag
            let cleanThinking = afterStart;
            const partialClose = /<\/?t?h?i?n?k?>?$/i.exec(cleanThinking);
            if (partialClose && cleanThinking.endsWith(partialClose[0])) {
                cleanThinking = cleanThinking.slice(0, -partialClose[0].length);
            }
            if (cleanThinking.trim()) {
                thinkingBlocks.push(cleanThinking.trim());
            }
            remaining = '';
            break;
        }

        // Closed <think> block
        const blockContent = afterStart.slice(0, endMatch.index).trim();
        if (blockContent) {
            thinkingBlocks.push(blockContent);
        }
        remaining = afterStart.slice(endMatch.index + endMatch[0].length);
    }

    const thinking = thinkingBlocks.join('\n\n').trim();
    const answer = answerBlocks.join('').trim();

    return { thinking, answer };
}

/**
 * Collapsible Thought Process Accordion (Option B).
 *
 * Keeps intermediate thinking steps, tool executions, and model reasoning
 * grouped inside a single collapsible accordion instead of stacking separate
 * chat bubbles.
 */
const ThoughtProcess = ({
    steps = [],
    currentStatus = '',
    thinkingText = '',
    isStreaming = false,
    hasAnswer = false,
    defaultOpen,
}) => {
    const [userToggled, setUserToggled] = useState(false);
    const [isOpenState, setIsOpenState] = useState(false);

    // Auto-open while actively thinking or when no answer is generated yet;
    // auto-collapse when answer is present.
    // If the user manually clicks, their toggle choice takes precedence.
    const isEffectivelyOpen = userToggled
        ? isOpenState
        : (defaultOpen !== undefined ? defaultOpen : (isStreaming || !hasAnswer));

    const handleToggle = () => {
        setUserToggled(true);
        setIsOpenState(!isEffectivelyOpen);
    };

    const hasSteps = Array.isArray(steps) && steps.length > 0;
    const hasThinkingText = Boolean(thinkingText && thinkingText.trim());

    // Title label
    let title = 'Thought process';
    if (isStreaming) {
        if (currentStatus) {
            title = currentStatus;
        } else if (hasSteps) {
            title = steps[steps.length - 1];
        } else {
            title = 'Thinking…';
        }
    } else if (hasSteps) {
        title = `Thought process (${steps.length} step${steps.length > 1 ? 's' : ''})`;
    }

    return (
        <div className={`sai-thought-process${isEffectivelyOpen ? ' sai-thought-process--open' : ''}${hasAnswer ? ' sai-thought-process--has-answer' : ''}`}>
            <button
                type="button"
                className="sai-thought-process__header"
                onClick={handleToggle}
                aria-expanded={isEffectivelyOpen}
                aria-label={title}
            >
                <span className="sai-thought-process__header-icon">
                    {isStreaming ? (
                        <Loader2 size={12} className="animate-spin" aria-hidden="true" />
                    ) : (
                        <Sparkles size={12} aria-hidden="true" />
                    )}
                </span>
                <span className="sai-thought-process__title">{title}</span>
                {isStreaming && (
                    <span className="sai-thought-process__pulse" aria-hidden="true" />
                )}
                <ChevronDown
                    size={12}
                    className={`sai-thought-process__chevron${
                        isEffectivelyOpen ? ' sai-thought-process__chevron--open' : ''
                    }`}
                    aria-hidden="true"
                />
            </button>

            {isEffectivelyOpen && (
                <div className="sai-thought-process__body">
                    {hasSteps && (
                        <ul className="sai-thought-process__steps">
                            {steps.map((step, idx) => {
                                const isLatest = idx === steps.length - 1;
                                const isRunning = isStreaming && isLatest;
                                return (
                                    <li
                                        key={idx}
                                        className={`sai-thought-process__step${
                                            isRunning ? ' sai-thought-process__step--running' : ' sai-thought-process__step--done'
                                        }`}
                                    >
                                        <span className="sai-thought-process__step-icon">
                                            {isRunning ? (
                                                <Loader2 size={11} className="animate-spin" aria-hidden="true" />
                                            ) : (
                                                <Check size={11} aria-hidden="true" />
                                            )}
                                        </span>
                                        <span className="sai-thought-process__step-text">{step}</span>
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    {hasThinkingText && (
                        <div className="sai-thought-process__text">
                            {thinkingText}
                        </div>
                    )}

                    {!hasSteps && !hasThinkingText && isStreaming && (
                        <div className="sai-thought-process__text sai-thought-process__text--muted">
                            Analyzing request and gathering information…
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ThoughtProcess;

